import { DashboardRule, optimizerServiceManager } from '@/optimizer';
import { OptimizerServiceManagerReturn } from '@/optimizer/createOptimizerServiceManager';
import { ReactNode, useCallback, useState } from 'react';
import { createContext } from 'use-context-selector';

const DEFAULT_OPTIMIZER_SERVICE_MANAGER: OptimizerServiceManagerReturn = {
    getDiminishingPoints: () => {
        return {};
    },
    getInitialAllocation: () => {
        return {};
    },
    getOptimizedPlan: async () => {
        return {} as any;
    },
    resetOptimisationRules: async () => {},
    getDefaultBudget: async () => 0,
    getBudgetLimits: async () => ({ lower: 0, upper: 0 }),
    getKPILimits: async () => ({ lower: 0, upper: 0 }),
    getTotalKPILimits: async () => ({ lower: 0, upper: 0 }),
    messages: async () => [],
    endDate: '',
    startDate: '',
    getOptimizerVersion: () => '',
    getBaseProjectMediaLimits: () => [],
    medias: [],
};

export const OptimizerContext = createContext<{
    optimizer: OptimizerServiceManagerReturn;
    initOptimizerServiceManager: () => void;
    optimizerReady: boolean;
    optimizerInitError: Error | null;
    setOptimizerInitError: (error: Error | null) => void;
    rules: DashboardRule[];
    setRules: (newRules: DashboardRule[]) => void;
    dashboardType: 'budget' | 'campaign';
    setDashboardType: (type: 'budget' | 'campaign') => void;
}>({
    optimizer: DEFAULT_OPTIMIZER_SERVICE_MANAGER,
    initOptimizerServiceManager: () => {},
    optimizerReady: false,
    optimizerInitError: null,
    setOptimizerInitError: () => {},
    rules: [],
    setRules: () => {},
    dashboardType: 'budget',
    setDashboardType: () => {},
});

export const OptimizerContextProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const [optimizerManager, setOptimizerServiceManager] =
        useState<OptimizerServiceManagerReturn>(
            DEFAULT_OPTIMIZER_SERVICE_MANAGER,
        );
    const [optimizerReady, setOptimizerReady] = useState(false);
    const [optimizerInitError, setOptimizerInitError] = useState<Error | null>(
        null,
    );

    const [rules, setRulesState] = useState<DashboardRule[]>([]);
    const setRules = useCallback((newRules: DashboardRule[]) => {
        setRulesState(newRules);
    }, []);

    const [dashboardType, setDashboardTypeState] = useState<
        'budget' | 'campaign'
    >('budget');
    const setDashboardType = useCallback((type: 'budget' | 'campaign') => {
        setDashboardTypeState(type);
    }, []);

    const initOptimizerServiceManager = useCallback(() => {
        setOptimizerInitError(null);
        setOptimizerReady(false);
        if (optimizerServiceManager) {
            setOptimizerServiceManager(optimizerServiceManager.serviceManager);
            setOptimizerReady(true);
        }
    }, []);

    return (
        <OptimizerContext.Provider
            value={{
                optimizer: optimizerManager,
                initOptimizerServiceManager,
                optimizerReady,
                optimizerInitError,
                setOptimizerInitError,
                rules,
                setRules,
                dashboardType,
                setDashboardType,
            }}
        >
            {children}
        </OptimizerContext.Provider>
    );
};
